const AUTH_API = process.env.REACT_APP_AUTH_API || "/api/auth";
const BASE = AUTH_API.replace("/api/auth", "");

const getToken = () => localStorage.getItem("admin_token") || "";

const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export const register = async (userData) => {
  const res = await fetch(`${AUTH_API}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(userData),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const login = async (email, password) => {
  const res = await fetch(`${AUTH_API}/admin-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

export const getOverview = async (days = "30") => {
  const params = new URLSearchParams();
  if (days) params.set("days", days);
  const res = await fetch(`${BASE}/api/admin/overview?${params}`, { headers: headers() });
  return res.json();
};

export const getUsers = async ({ page = 1, limit = 50, search = "" } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set("search", search);
  const res = await fetch(`${BASE}/api/admin/users?${params}`, { headers: headers() });
  return res.json();
};

export const deleteUser = async (userId) => {
  const res = await fetch(`${BASE}/api/admin/users/${userId}`, {
    method: "DELETE",
    headers: headers(),
  });
  return res.json();
};

export const updateUserRole = async (userId, role) => {
  const res = await fetch(`${BASE}/api/users/${userId}/role`, {
    method: "PUT",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  });
  return res.json();
};

export const updateUserPlan = async (userId, planId) => {
  const res = await fetch(`${BASE}/api/admin/users/${userId}/plan`, {
    method: "PUT",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify({ planId }),
  });
  return res.json();
};

export const getPlans = async () => {
  const res = await fetch(`${BASE}/api/plans`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch plans");
  return res.json();
};

export const createPlan = async (data) => {
  const res = await fetch(`${BASE}/api/admin/plans`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

export const updatePlan = async (id, data) => {
  const res = await fetch(`${BASE}/api/admin/plans/${id}`, {
    method: "PUT",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

export const deletePlan = async (id) => {
  const res = await fetch(`${BASE}/api/admin/plans/${id}`, {
    method: "DELETE",
    headers: headers(),
  });
  return res.json();
};

export const getBots = async ({ page = 1, limit = 50, search = "" } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set("search", search);
  const res = await fetch(`${BASE}/api/admin/bots?${params}`, { headers: headers() });
  return res.json();
};

export const createBot = async (data) => {
  const res = await fetch(`${BASE}/api/bots`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

export const updateBot = async (botId, data) => {
  const res = await fetch(`${BASE}/api/bots/${botId}`, {
    method: "PUT",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  return res.json();
};

export const deleteBot = async (botId) => {
  const res = await fetch(`${BASE}/api/admin/bots/${botId}`, {
    method: "DELETE",
    headers: headers(),
  });
  return res.json();
};

export const getUserDetail = async (userId) => {
  const res = await fetch(`${BASE}/api/admin/users/${userId}`, {
    headers: headers(),
  });
  return res.json();
};

export const getConversations = async ({ page = 1, limit = 50, platform = "" } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (platform) params.set("platform", platform);
  const res = await fetch(`${BASE}/api/admin/conversations?${params}`, { headers: headers() });
  return res.json();
};

export const getPlatformStats = async (platform = "", days = "30") => {
  const params = new URLSearchParams();
  if (platform) params.set("platform", platform);
  if (days) params.set("days", days);
  const res = await fetch(`${BASE}/api/admin/platform-stats?${params}`, { headers: headers() });
  return res.json();
};

// ---------- WhatsApp Admin API ----------

export const getWhatsAppDashboard = async () => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/dashboard`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch WhatsApp dashboard");
  return res.json();
};

export const getWhatsAppResellers = async () => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/resellers`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch resellers");
  return res.json();
};

export const createWhatsAppReseller = async (data) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/resellers`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to create reseller");
  return result;
};

export const getWhatsAppClients = async () => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch clients");
  return res.json();
};

export const getSuspendedClients = async () => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/suspended-clients`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch suspended clients");
  return res.json();
};

export const getWhatsAppClientDashboard = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/dashboard`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch client dashboard");
  return res.json();
};

export const getClientChats = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/chats`, { headers: headers() });
  if (!res.ok) throw new Error("Failed to fetch chat history");
  return res.json();
};

export const migrateClientChats = async (clientId, data = {}) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/chats/migrate`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to migrate chats");
  return result;
};

export const diagnoseWhatsAppClient = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/diagnose`, { headers: headers() });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to diagnose client");
  return result;
};

export const syncWhatsAppDisplayNumber = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/sync-number`, {
    method: "POST",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to sync display number");
  return result;
};

export const switchWhatsAppClientNumber = async (clientId, data) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/switch-number`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to switch number");
  return result;
};

export const resubscribeWhatsAppWebhook = async (clientId, data) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/re-subscribe`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to re-subscribe webhook");
  return result;
};

export const testWhatsAppWebhook = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/webhook-test`, {
    method: "POST",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to run webhook self-test");
  return result;
};

export const verifyWhatsAppClient = async (clientId, data) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/verify`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to verify client");
  return result;
};

export const checkWebhookStatus = async (wabaId, clientAccessToken = "") => {
  const params = new URLSearchParams({ wabaId });
  if (clientAccessToken) params.append("clientAccessToken", clientAccessToken);
  const res = await fetch(`${BASE}/api/whatsapp/admin/webhook-status?${params}`, { headers: headers() });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to check webhook status");
  return result;
};

export const getWabaOwnerInfo = async (clientId, wabaId, clientAccessToken = "") => {
  const params = new URLSearchParams({ wabaId });
  if (clientAccessToken) params.append("clientAccessToken", clientAccessToken);
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/waba-owner?${params}`, { headers: headers() });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to fetch WABA owner info");
  return result;
};

export const approveWhatsAppClient = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/approve`, {
    method: "POST",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to approve client");
  return result;
};

export const suspendWhatsAppClient = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/suspend`, {
    method: "POST",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to suspend client");
  return result;
};

export const reactivateWhatsAppClient = async (clientId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/clients/${clientId}/reactivate`, {
    method: "POST",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to reactivate client");
  return result;
};

export const updatePhoneNumberId = async (userId, data) => {
  const res = await fetch(`${BASE}/api/admin/users/${userId}/phone-number-id`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to save Phone Number ID");
  return result;
};

export const deletePendingVerification = async (accountId) => {
  const res = await fetch(`${BASE}/api/whatsapp/admin/pending-verifications/${accountId}`, {
    method: "DELETE",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to delete pending verification");
  return result;
};

// ---------- Earnings (Finance) API ----------

export const getFinanceSummary = async () => {
  const res = await fetch(`${BASE}/api/admin/finance`, { headers: headers() });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to load earnings");
  return result;
};

export const addFinanceEntry = async (data) => {
  const res = await fetch(`${BASE}/api/admin/finance`, {
    method: "POST",
    headers: { ...headers(), "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to add entry");
  return result;
};

export const deleteFinanceEntry = async (id) => {
  const res = await fetch(`${BASE}/api/admin/finance/${id}`, {
    method: "DELETE",
    headers: headers(),
  });
  const result = await res.json();
  if (!res.ok) throw new Error(result.message || "Failed to delete entry");
  return result;
};
